import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { api, refreshSession, tokenStore } from "@/api/client";
import type { SignupOut, TokenOut } from "@/api/types";

type Status = "loading" | "authed" | "guest";

export interface SignupInput {
  company_name: string; workspace: string; full_name: string; email: string; password: string;
  currency?: string; timezone?: string; accept_terms: boolean;
}

interface AuthState {
  status: Status;
  login(workspace: string, email: string, password: string): Promise<void>;
  /** Resolves to "pending" when the workspace must be approved before anyone can sign in. */
  signup(data: SignupInput): Promise<"active" | "pending">;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    const unsubscribe = tokenStore.subscribe((authed) => setStatus(authed ? "authed" : "guest"));
    void refreshSession().then((ok) => setStatus(ok ? "authed" : "guest"));
    return unsubscribe;
  }, []);

  const login = useCallback(async (workspace: string, email: string, password: string) => {
    const res = await api<TokenOut>("POST", "/auth/login", { workspace, email, password });
    qc.clear();
    tokenStore.set(res.access_token);
  }, [qc]);

  const signup = useCallback(async (data: SignupInput) => {
    const res = await api<SignupOut>("POST", "/auth/signup", { ...data, industry_pack: "manufacturing" });
    if (res.status === "pending_approval" || !res.access_token) return "pending";
    qc.clear();
    tokenStore.set(res.access_token);
    return "active";
  }, [qc]);

  const logout = useCallback(async () => {
    try { await api<void>("POST", "/auth/logout"); } finally {
      tokenStore.set(null);
      qc.clear();
    }
  }, [qc]);

  const value = useMemo(() => ({ status, login, signup, logout }), [status, login, signup, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
